#!/usr/bin/env node
/* eslint-disable @typescript-eslint/no-explicit-any */
import type { TransactionReceipt } from "@saberhq/solana-contrib";
import { PublicKey } from "@saberhq/solana-contrib";
import { Command } from "commander";
import { exit } from "process";
import invariant from "tiny-invariant";

import * as pack from "../package.json";
import { QUARRY_CODERS } from "../src";
import { ActivityErrors } from "../src/idls/activity";
import {
  findActivityMasterAddress,
  findActivityMetadataAddress,
} from "../src/wrappers/activity/pda";
import { makeSDK, printObjectJSON, printObjectTable } from "./utils";

export const program = new Command().name("crema-acitity");
program.version(pack.version);

program
  .command("master-key")
  .description("Get activity master pda key")
  .action(() => {
    getMasterPda()
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

program
  .command("master-info")
  .description("Get activity master info")
  .argument("<master>", "activity master")
  .action((master: string) => {
    console.log(master);
    fetchActivitymaster(new PublicKey(master))
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

program
  .command("master-time")
  .description("Get mint and claim finish time")
  .argument("<master>", "activity master")
  .action((master: string) => {
    fetchTimeInfo(new PublicKey(master))
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

program
  .command("crema-key-info")
  .description("Get info of 5 level keys info")
  .argument("<master>", "activity master")
  .action((master: string) => {
    fetchCremaKeysInfo(new PublicKey(master))
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

program
  .command("second-party-info")
  .description("Get info of second party")
  .argument("<master>", "activity master")
  .action((master: string) => {
    fetchSecondPartyInfo(new PublicKey(master))
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

program
  .command("metadata-info")
  .description("Get activity medatata info")
  .argument("<mint>", "nft mint key")
  .action((mint: string) => {
    fetchActivityMetadata(new PublicKey(mint))
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

program
  .command("crema-keys")
  .description("Get crema keys of user")
  .argument("<user>", "user address")
  .action((user: string) => {
    fetchCremakeys(new PublicKey(user))
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

program
  .command("add-transfer")
  .description(
    "Add second party transfer. wrapper, transferer, mint, exchange_rate, price_decimal, name is needed.\n \
    Please Be Careful !!!!! The Last Two Position of Transfers [16 array], Which is index of 14 and 15, is used for save mint and claim end time. \n \
    index 14: mint end time; index 15: claim end time"
  )
  .option("-a, --authority <authority>", "The activity program authority")
  .option("-w, --wrapper <wrapper>", "The transfer wrapper")
  .option("-t, --transferer <transferer>", "The transferer")
  .option("-m, --mint <mint>", "source token mint")
  .option("-idx, --idx <idx>", "index of the transfer")
  .option("-f, --exchange_rate <exchange_rate>", "exchange_rate")
  .option("-d, --decimal <decimal>", "decimal")
  .option("-n, --name <name>", "name")
  .action((arg: any) => {
    const authority = new PublicKey(arg.authority);
    const wrapper = new PublicKey(arg.wrapper);
    const transferer = new PublicKey(arg.transferer);
    const token_mint = new PublicKey(arg.mint);
    const idx: number = arg.idx;
    const exchange_rate: number = arg.exchange_rate;
    const decimal: number = arg.decimal;
    const name: string = arg.name;
    addTransfer(
      authority,
      wrapper,
      transferer,
      token_mint,
      idx,
      exchange_rate,
      decimal,
      name
    )
      .then()
      .catch((err) => {
        console.log(err);
      });
  });

// Init acvitity program.
program
  .command("init")
  .description("Init acvitity program")
  .option("-m, --mint <mint>", "caffeineTokenMint")
  .option("-mw, --mintWrapper <mintWrapper>", "mintWrapper")
  .option("-mt, --minter <minter>", "minter")
  .option(
    "-t, --openRewardTimestamp <openRewardTimestamp>",
    "open Reward Timestamp"
  )
  .option("-u, --url <url>", "nft url")
  .option("-p, --partyNum <partyNum>", "second party num")
  .option("-f, --exchange_rate <exchange_rate>", "exchange_rate")
  .option("-d, --decimal <decimal>", "decimal")
  .action((arg: any) => {
    const caffeineTokenMint = new PublicKey(arg.mint);
    const mintWrapper = new PublicKey(arg.mintWrapper);
    const minter = new PublicKey(arg.minter);
    const openRewardTimestamp: number = arg.openRewardTimestamp;
    const url: string = arg.url;
    const secondPartyNum: number = arg.partyNum;
    const exchange_rate: number = arg.exchange_rate;
    const decimal: number = arg.decimal;
    initActivity(
      caffeineTokenMint,
      mintWrapper,
      minter,
      openRewardTimestamp,
      url,
      secondPartyNum,
      exchange_rate,
      decimal,
      [2, 3, 4, 5, 6]
    )
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

program
  .command("update")
  .description("Update open reward timestamp and url[optional]")
  .option("-a, --authority <authority>", "The activity program authority")
  .option("-t, --timestamp [timestamp]", "new open Reward Timestamp")
  .option("-u, --url [url]", "nft url")
  .option("-p, --second_party [second_party]", "second party num")
  .option("-f, --exchange_rate [exchange_rate]", "exchange_rate")
  .option("-d, --decimal [decimal]", "decimal")
  .option("-mw, --mintWrapper [mintWrapper]", "mintWrapper")
  .option("-mt, --minter [minter]", "minter")
  .action((arg: any) => {
    const w = new PublicKey(arg.authority);
    const timestamp: number = arg.timestamp;
    const url: string = arg.url;
    const p: number = arg.second_party;
    const exchange_rate = Number(arg.exchange_rate);
    const decimal = Number(arg.decimal);
    let mintWrapper = null;
    if (arg.mintWrapper !== null && arg.mintWrapper !== undefined) {
      mintWrapper = new PublicKey(arg.mintWrapper);
    }
    let minter = null;
    if (arg.minter !== null && arg.minter !== undefined) {
      minter = new PublicKey(arg.minter);
    }
    updateActivity(
      w,
      timestamp,
      url,
      p,
      exchange_rate,
      decimal,
      mintWrapper,
      minter
    )
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

program
  .command("mint")
  .description("Mint nft use caffeine")
  .option("-u --user <user>", "The user key")
  .option("-d, --degree <degree>", "The degree of nft want to mint")
  .action((arg: any) => {
    const w = new PublicKey(arg.user);
    const degree: number = arg.degree;
    mint(w, degree)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

program
  .command("upgrade")
  .description("Upgrade nft")
  .option("-u --user <user>", "The user key")
  .option("-m, --mint <mint>", "The mint of nft want to upgrade")
  .action((arg: any) => {
    const user: PublicKey = new PublicKey(arg.user);
    const mint: PublicKey = new PublicKey(arg.mint);
    upgrade(user, mint)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

program
  .command("claim")
  .description("Claim reward use nft")
  .option("-u --user <user>", "The user key")
  .option("-m, --mint <mint>", "The mint of nft want to claim")
  .action((arg: any) => {
    const user: PublicKey = new PublicKey(arg.user);
    const mint: PublicKey = new PublicKey(arg.mint);
    claim(user, mint)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

program
  .command("claim_second_party")
  .description("Claim second party token reward use nft")
  .option("-u --user <user>", "The user key")
  .option("-m, --mint <mint>", "The mint of nft want to claim")
  .option("-t, --token <token>", "The mint of token want to claim")
  .action((arg: any) => {
    const user: PublicKey = new PublicKey(arg.user);
    const mint: PublicKey = new PublicKey(arg.mint);
    const token_mint: PublicKey = new PublicKey(arg.token);
    claimSecondParty(user, mint, token_mint)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

program
  .command("claim_caffeine")
  .description("Claim redundant caffeine")
  .option("-u --user <user>", "The user key")
  .option(
    "-m --amount <amount>",
    "amount, amount should contains decimals, exp: if decimals is 6, 200.13 token,the amount should be 200130000"
  )
  .action((arg: any) => {
    const user: PublicKey = new PublicKey(arg.user);
    const amount: number = arg.amount;
    claimCaffeine(user, amount)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

program
  .command("close")
  .description("close")
  .option("-a, --authority <authority>", "The activity program authority")
  .action((arg: any) => {
    const authority = new PublicKey(arg.authority);
    close(authority)
      .then()
      .catch((err) => {
        console.log(err);
      });
  });

async function fetchActivitymaster(master: PublicKey) {
  const sdk = makeSDK();
  const info = await sdk.activity.fetchActivitymaster(master);
  if (info === null) {
    console.log("Activitymaster %s not found", master.toBase58());
    return;
  }
  console.log(typeof ActivityErrors);
  printObjectTable(info);
}

async function fetchCremaKeysInfo(master: PublicKey) {
  const sdk = makeSDK();
  const info = await sdk.activity.fetchActivitymaster(master);
  if (info === null) {
    console.log("Activitymaster %s not found", master.toBase58());
    return;
  }
  console.log(typeof ActivityErrors);
  for (const e of info.cremaKeys) {
    printObjectTable(e);
  }
}

async function fetchSecondPartyInfo(master: PublicKey) {
  const sdk = makeSDK();
  const info = await sdk.activity.fetchActivitymaster(master);
  if (info === null) {
    console.log("Activitymaster %s not found", master.toBase58());
    return;
  }
  console.log(typeof ActivityErrors);
  for (let i = 0; i < info.secondPartyNum; i++) {
    console.log("transferer", i);
    printObjectTable(info.transfers[i]);
  }
}
//get claim and mint finish time. !!! the last two transfers is used for these two time new haha needs.
async function fetchTimeInfo(master: PublicKey) {
  const sdk = makeSDK();
  const info = await sdk.activity.fetchActivitymaster(master);
  if (info === null) {
    console.log("Activitymaster %s not found", master.toBase58());
    return;
  }
  console.log(typeof ActivityErrors);
  printObjectTable({
    mintFinishTime: info.transfers[info.transfers.length - 2]?.exchangeRate,
    claimFinishTime: info.transfers[info.transfers.length - 1]?.exchangeRate,
  });
}

async function fetchActivityMetadata(mint: PublicKey) {
  const sdk = makeSDK();
  const [activityMedatata, _] = await findActivityMetadataAddress(mint);
  const info = await sdk.activity.fetchMetadata(activityMedatata);
  if (info === null) {
    console.log(
      "activityMedatata %s not found fot this nft",
      activityMedatata.toBase58()
    );
    return;
  }
  console.log(typeof ActivityErrors);
  printObjectTable({
    caffeine: info.caffeine,
    degree: info.degree,
    mint: info.mint.toBase58(),
    mintUser: info.mintUser.toBase58(),
    isCrmClaimed: info.isCrmClaimed,
    seed: info.seed,
    crmClaimedAmount: info.crmClaimedAmount,
    isSecondPartyClaimed: info.isClaimed,
    claimAmounts: info.claimAmounts,
    activityMedatata: activityMedatata.toBase58(),
  });
}

async function fetchCremakeys(user: PublicKey) {
  const sdk = makeSDK();
  const info = await sdk.activity.fetchCremaKeysV2(
    sdk.provider.connection,
    user
  );
  console.log(info);
}

async function getMasterPda() {
  const pda = await findActivityMasterAddress();
  printObjectJSON({ activityMaster: pda });
}

async function initActivity(
  caffeineTokenMint: PublicKey,
  mintWrapper: PublicKey,
  minter: PublicKey,
  openRewardTimestamp: number,
  url: string,
  secondPartyNum: number,
  crmExchangeRate: number,
  crmPriceDecimal: number,
  multipliers: number[]
): Promise<TransactionReceipt> {
  const sdk = makeSDK();
  const authority = sdk.provider.walletKey;
  const tx = await sdk.activity.init({
    authority,
    caffeineTokenMint,
    mintWrapper,
    minter,
    openRewardTimestamp,
    url,
    secondPartyNum,
    crmExchangeRate,
    crmPriceDecimal,
    multipliers,
  });
  const receipt = await tx.tx.confirm();

  printObjectJSON({
    signature: receipt.signature.toString(),
    computeUnits: receipt.computeUnits,
    activityMaster: tx.activityMaster.toBase58(),
    authority: tx.authority.toBase58(),
  });
  return receipt;
}

async function updateActivity(
  authority: PublicKey,
  timestampNow: number | null,
  url: string | null,
  op_second_party_num: null | number,
  op_crm_exchange_rate: number | null,
  op_crm_price_decimal: number | null,
  mintWrapper: PublicKey | null,
  minter: PublicKey | null
) {
  const sdk = makeSDK();
  const authoritySdk = sdk.provider.walletKey;
  invariant(
    authority.toBase58() === authoritySdk.toBase58(),
    "authority is the same to sdk"
  );
  const tx = await sdk.activity.update({
    authority,
    timestampNow,
    url,
    op_second_party_num,
    op_crm_exchange_rate,
    op_crm_price_decimal,
    mintWrapper,
    minter,
  });
  const receipt = await tx.confirm();
  printObjectJSON({
    signature: receipt.signature.toString(),
    computeUnits: receipt.computeUnits,
    timestampNow:
      timestampNow !== null && timestampNow !== undefined
        ? timestampNow
        : "timestampNow not updated",
    url: url !== null && url !== undefined ? url : "url not updated",
    op_second_party_num:
      op_second_party_num !== undefined
        ? op_second_party_num
        : "second_party_num not updated",
    op_crm_exchange_rate:
      op_crm_exchange_rate !== undefined
        ? op_crm_exchange_rate
        : "crm_exchange_rate not updated",

    op_crm_price_decimal:
      op_crm_price_decimal !== undefined
        ? op_crm_price_decimal
        : "crm_price_decimal not updated",
    mintWrapper:
      mintWrapper !== undefined && mintWrapper !== null
        ? mintWrapper?.toBase58()
        : "mintWrapper not updated",
    minter:
      minter !== undefined && minter !== null
        ? minter?.toBase58()
        : "minter not updated",
  });
  return receipt;
}

async function mint(user: PublicKey, degree: number) {
  const sdk = makeSDK();
  const authoritySdk = sdk.provider.walletKey;

  invariant(
    user.toBase58() === authoritySdk.toBase58(),
    "user is the same to sdk"
  );
  const tx = await sdk.activity.mintCremaKey({
    user,
    degree,
  });
  const receipt = await tx.tx.confirm();
  const event = QUARRY_CODERS.Activity.parseProgramLogEvents(
    receipt.response.meta?.logMessages ?? []
  )[0];
  invariant(event?.name === "MintEvent", "MintEvent not found");

  printObjectJSON({
    signature: receipt.signature.toString(),
    computeUnits: receipt.computeUnits,
    mint: tx.mint.toBase58(),
    user: user.toBase58(),
    receiverTokenAccount: tx.receiverTokenAccount.toBase58(),
    metatada: tx.metadata.toBase58(),
    activityMetatdata: tx.activityMetadata.toBase58(),
  });
  return receipt;
}

async function upgrade(user: PublicKey, mint: PublicKey) {
  const sdk = makeSDK();
  const authoritySdk = sdk.provider.walletKey;

  invariant(
    user.toBase58() === authoritySdk.toBase58(),
    "user is the same to sdk"
  );
  const tx = await sdk.activity.upgrade({
    user,
    mint,
  });
  const receipt = await tx.confirm();
  const event = QUARRY_CODERS.Activity.parseProgramLogEvents(
    receipt.response.meta?.logMessages ?? []
  )[0];
  invariant(event?.name === "UpgradeEvent", "UpgradeEvent not found");
  printObjectJSON({
    signature: receipt.signature.toString(),
    computeUnits: receipt.computeUnits,
    mint: mint.toBase58(),
    user: user.toBase58(),
  });
  return receipt;
}

async function claim(user: PublicKey, mint: PublicKey) {
  const sdk = makeSDK();
  const authoritySdk = sdk.provider.walletKey;

  invariant(
    user.toBase58() === authoritySdk.toBase58(),
    "user is the same to sdk"
  );
  const tx = await sdk.activity.claimReward({
    user,
    mint,
  });

  const crmClaim = await tx.confirm();
  const event = QUARRY_CODERS.Activity.parseProgramLogEvents(
    crmClaim.response.meta?.logMessages ?? []
  )[0];
  printObjectJSON({
    name: "crmClaim",
    signature: crmClaim.signature.toString(),
    computeUnits: crmClaim.computeUnits,
    mint: mint.toBase58(),
    user: user.toBase58(),
  });
}

async function claimSecondParty(
  user: PublicKey,
  mint: PublicKey,
  token_mint: PublicKey
) {
  const sdk = makeSDK();
  const authoritySdk = sdk.provider.walletKey;

  invariant(
    user.toBase58() === authoritySdk.toBase58(),
    "user is the same to sdk"
  );
  const tx = await sdk.activity.claimSecondParty({
    user,
    mint,
    token_mint,
  });
  invariant(tx !== null);
  const crmClaim = await tx.confirm();
  const event = QUARRY_CODERS.Activity.parseProgramLogEvents(
    crmClaim.response.meta?.logMessages ?? []
  )[0];
  printObjectJSON({
    name: "secondPartyClaim",
    signature: crmClaim.signature.toString(),
    computeUnits: crmClaim.computeUnits,
    mint: mint.toBase58(),
    user: user.toBase58(),
  });
}

async function claimCaffeine(user: PublicKey, amount: number) {
  const sdk = makeSDK();
  const authoritySdk = sdk.provider.walletKey;
  invariant(
    user.toBase58() === authoritySdk.toBase58(),
    "authority is the same to sdk"
  );
  const tx = await sdk.activity.claimCaffeine({
    user,
    amount,
  });
  const receipt = await tx.confirm();
  printObjectJSON({
    signature: receipt.signature.toString(),
    computeUnits: receipt.computeUnits,
  });
  return receipt;
}

async function close(authority: PublicKey) {
  const sdk = makeSDK();
  const authoritySdk = sdk.provider.walletKey;
  invariant(
    authority.toBase58() === authoritySdk.toBase58(),
    "authority is the same to sdk"
  );
  const tx = await sdk.activity.close({
    authority,
  });
  const receipt = await tx.confirm();
  printObjectJSON({
    signature: receipt.signature.toString(),
    computeUnits: receipt.computeUnits,
  });
  return receipt;
}

async function addTransfer(
  authority: PublicKey,
  wrapper: PublicKey,
  transferer: PublicKey,
  tokenMint: PublicKey,
  idx: number,
  exchange_rate: number,
  decimal: number,
  name: string
) {
  const sdk = makeSDK();
  const authoritySdk = sdk.provider.walletKey;
  invariant(
    authority.toBase58() === authoritySdk.toBase58(),
    "authority is the same to sdk"
  );
  const tx = await sdk.activity.addTransfer({
    authority,
    wrapper,
    transferer,
    tokenMint,
    idx,
    exchange_rate,
    decimal,
    name,
  });
  const receipt = await tx.confirm();
  printObjectJSON({
    signature: receipt.signature.toString(),
    computeUnits: receipt.computeUnits,
  });
  return receipt;
}
